/**
 * @module TTProductTabsCustom.Mapper
 * Normalizes PDP item data for tabs and decides visibility / default active tab.
 *
 * IMPORTANT — field availability in the frontend:
 * Custom fields must be exposed to the website in NetSuite (product field sets
 * / Commerce Catalog). If a field is not in the item JSON in the browser, it will be
 * `undefined` here and the UI will skip it without error. There is no way from JS to request
 * a custitem the backend does not send: add it to the field set and publish.
 *
 * Where to map more fields:
 * - SPEC_FIELD_GROUPS: specification rows (field + label).
 * - DOCUMENT_SLOT_COUNT / VIDEO_SLOT_COUNT: extend ranges if you add more slots.
 * - Videos: custitem_tt_video_src_* must be the YouTube video ID only (not a full URL).
 * - Product Category pills: item field `class` (expose in Commerce field set). Supports a single
 *   ref object, CSV string, or array of strings / { name, text } objects (multi-select). Each
 *   segment is further split on ":" into separate pills (trimmed).
 * - TAB_LABEL_KEYS (View): tab id → key passed to Utils.translate.
 */
define('TTProductTabsCustom.Mapper'
,	[]
,	function ()
{
	'use strict';

	/** Set to false before production deploy */
	var DEBUG_LOG = true;

	var LOG_PREFIX = '[TTProductTabsCustom.Mapper]';

	/** Fields to audit in the console (presence on the plain record) */
	var TRACKED_FIELD_KEYS = [
		'storedetaileddescription'
	,	'custitem_packship_interdescription'
	,	'custitem_tt_power'
	,	'custitem_tt_output_voltage'
	,	'custitem_tt_output_current'
	,	'custitem_tt_output_type'
	,	'custitem_tt_dimensions'
	,	'custitem_tt_weight'
	,	'custitem_tt_cooling'
	,	'custitem_tt_compliance'
	,	'custitem5'
	,	'custitem_tt_country_origin'
	,	'custitem_tt_warranty'
	,	'custitem_tt_features'
	,	'custitem6'
	,	'custitem_tt_document_title_1'
	,	'custitem_tt_document_file_src_1'
	,	'custitem_tt_document_title_2'
	,	'custitem_tt_document_file_src_2'
	,	'custitem_tt_document_title_3'
	,	'custitem_tt_document_file_src_3'
	,	'custitem_tt_document_title_4'
	,	'custitem_tt_document_file_src_4'
	,	'custitem_tt_video_title_1'
	,	'custitem_tt_video_src_1'
	,	'custitem_tt_video_img_1'
	,	'custitem_tt_video_title_2'
	,	'custitem_tt_video_src_2'
	,	'custitem_tt_video_img_2'
	,	'custitem_tt_video_alt_1'
	,	'custitem_tt_video_alt_2'
	,	'class'
	];

	function debugLog(message, payload)
	{
		if (!DEBUG_LOG || typeof console === 'undefined' || !console.log)
		{
			return;
		}
		if (payload !== undefined)
		{
			console.log(LOG_PREFIX + ' ' + message, payload);
		}
		else
		{
			console.log(LOG_PREFIX + ' ' + message);
		}
	}

	function logFieldPresence(plainItem)
	{
		if (!DEBUG_LOG || !plainItem || typeof plainItem !== 'object')
		{
			return;
		}
		var presence = {};
		for (var i = 0; i < TRACKED_FIELD_KEYS.length; i++)
		{
			var k = TRACKED_FIELD_KEYS[i];
			var v = getRawField(plainItem, k);
			presence[k] = v !== undefined && v !== null && v !== '';
		}
		debugLog('Field presence (true = non-empty value on record):', presence);
	}

	/**
	 * Converts the PDP item (plain object or Backbone.Model) to a plain object for consistent reads.
	 */
	function toPlainItemRecord(item)
	{
		if (item == null)
		{
			return null;
		}
		if (typeof item.toJSON === 'function')
		{
			try
			{
				var json = item.toJSON();
				if (json && typeof json === 'object')
				{
					return json;
				}
			}
			catch (e1)
			{
				debugLog('toJSON() failed, falling back to attributes/object', e1);
			}
		}
		if (item.attributes && typeof item.attributes === 'object' && !Array.isArray(item.attributes))
		{
			return item.attributes;
		}
		return item;
	}

	// --- Tab ids (stable for logic and template) ---
	var TAB_IDS = {
		DESCRIPTION: 'description'
	,	FEATURES_AND_SPECS: 'featuresAndSpecs'
	,	DOCUMENTS: 'documents'
	,	VIDEOS: 'videos'
	};

	// Priority order for choosing the default active tab
	var TAB_PRIORITY = [
		TAB_IDS.DESCRIPTION
	,	TAB_IDS.FEATURES_AND_SPECS
	,	TAB_IDS.DOCUMENTS
	,	TAB_IDS.VIDEOS
	];

	/**
	 * Specifications: sections and rows. Edit labels here or move to translations in the View.
	 */
	var SPEC_FIELD_GROUPS = [
		{
			sectionTitle: 'Power & Efficiency'
		,	rows: [
				{ field: 'custitem_tt_power', label: 'Maximum Power' }
			]
		}
	,	{
			sectionTitle: 'Electrical Characteristics'
		,	rows: [
				{ field: 'custitem_tt_output_voltage', label: 'Output Voltage' }
			,	{ field: 'custitem_tt_output_current', label: 'Output Current' }
			,	{ field: 'custitem_tt_output_type', label: 'Output Type' }
			]
		}
	,	{
			sectionTitle: 'Physical Dimensions'
		,	rows: [
				{ field: 'custitem_tt_dimensions', label: 'Dimensions' }
			,	{ field: 'custitem_tt_weight', label: 'Weight' }
			,	{ field: 'custitem_tt_cooling', label: 'Cooling' }
			]
		}
	,	{
			sectionTitle: 'Compliance & Certifications'
		,	rows: [
				{ field: 'custitem_tt_compliance', label: 'Compliance' }
			,	{ field: 'custitem5', label: 'Certificates' }
			,	{ field: 'custitem_tt_country_origin', label: 'Country of Origin' }
			]
		}
	,	{
			sectionTitle: 'Warranty Information'
		,	rows: [
				{ field: 'custitem_tt_warranty', label: 'Warranty' }
			]
		}
	];

	var DOCUMENT_SLOT_COUNT = 4;
	var VIDEO_SLOT_COUNT = 2;

	/**
	 * Reads a raw value from the item model; tolerates different shapes (list/record, objects, etc.).
	 * @param {Object} item
	 * @param {String} key
	 * @return {*} raw value or undefined
	 */
	function getRawField(item, key)
	{
		if (!item || !key)
		{
			return undefined;
		}
		try
		{
			if (typeof item.get === 'function')
			{
				var got = item.get(key);
				if (got !== undefined)
				{
					return got;
				}
			}
			if (item.attributes && Object.prototype.hasOwnProperty.call(item.attributes, key))
			{
				return item.attributes[key];
			}
			if (Object.prototype.hasOwnProperty.call(item, key))
			{
				return item[key];
			}
			if (key in item)
			{
				return item[key];
			}
		}
		catch (e)
		{
			return undefined;
		}
		return undefined;
	}

	/**
	 * Converts a NetSuite / SCA field value to a display string (not HTML).
	 * @param {*} raw
	 * @return {String|null} null if there is no useful value
	 */
	function toDisplayString(raw)
	{
		if (raw === null || raw === undefined)
		{
			return null;
		}
		if (typeof raw === 'boolean')
		{
			return raw ? 'Yes' : 'No';
		}
		if (typeof raw === 'number' && !isNaN(raw))
		{
			return String(raw);
		}
		if (typeof raw === 'string')
		{
			var t = raw.replace(/^\s+|\s+$/g, '');
			return t.length ? t : null;
		}
		if (Array.isArray(raw))
		{
			var parts = [];
			for (var i = 0; i < raw.length; i++)
			{
				var p = toDisplayString(raw[i]);
				if (p)
				{
					parts.push(p);
				}
			}
			return parts.length ? parts.join(', ') : null;
		}
		if (typeof raw === 'object')
		{
			if (raw.value !== undefined && raw.value !== null && raw.value !== '')
			{
				return toDisplayString(raw.value);
			}
			if (raw.text !== undefined && raw.text !== null && raw.text !== '')
			{
				return toDisplayString(raw.text);
			}
			if (raw.name !== undefined && raw.name !== null && raw.name !== '')
			{
				return toDisplayString(raw.name);
			}
			if (raw.internalid !== undefined && raw.internalid !== null && raw.internalid !== '')
			{
				return toDisplayString(raw.internalid);
			}
		}
		return null;
	}

	/**
	 * String for href/src attributes (URLs): same rules as display but without forcing plain text.
	 */
	function toUrlString(raw)
	{
		var s = toDisplayString(raw);
		if (s)
		{
			return s;
		}
		if (typeof raw === 'string' && raw.indexOf('http') === 0)
		{
			return raw;
		}
		return null;
	}

	/**
	 * Value for HTML blocks (description / features): keeps HTML when provided as a string.
	 */
	function toHtmlString(raw)
	{
		if (raw === null || raw === undefined)
		{
			return null;
		}
		if (typeof raw === 'string')
		{
			var t = raw.replace(/^\s+|\s+$/g, '');
			return t.length ? raw : null;
		}
		if (typeof raw === 'object' && raw.text !== undefined)
		{
			return toHtmlString(raw.text);
		}
		return null;
	}

	/**
	 * Plain text used to detect whether HTML has meaningful content.
	 */
	function htmlToPlainText(html)
	{
		if (!html || typeof html !== 'string')
		{
			return '';
		}
		return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
	}

	function hasHtmlContent(html)
	{
		return htmlToPlainText(html || '').length > 0;
	}

	/**
	 * Normalizes custitem_tt_video_src_* values: expects a YouTube video ID only (not a full URL).
	 * Typical IDs are 11 chars [A-Za-z0-9_-]; allow 6–32 for minor variance.
	 * @param {*} raw
	 * @return {String|null}
	 */
	function normalizeYoutubeVideoId(raw)
	{
		var s = toDisplayString(raw);
		if (!s)
		{
			return null;
		}
		if (/[/?&=]/.test(s))
		{
			debugLog('normalizeYoutubeVideoId: value looks like a URL; store only the video ID in custitem_tt_video_src_*', s);
			return null;
		}
		if (!/^[a-zA-Z0-9_-]{6,32}$/.test(s))
		{
			debugLog('normalizeYoutubeVideoId: invalid id format', s);
			return null;
		}
		return s;
	}

	/**
	 * Safe embed URL for a validated YouTube video id.
	 * @param {String} videoId
	 * @return {String} empty string if id is invalid
	 */
	function buildYoutubeEmbedUrl(videoId)
	{
		var id = normalizeYoutubeVideoId(videoId);
		if (!id)
		{
			return '';
		}
		return 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
	}

	/**
	 * Treat values that are only whitespace, &nbsp; (entity or char), or HTML that is empty when stripped as missing.
	 */
	function isVacuousSpecificationValue (val)
	{
		if (val == null)
		{
			return true;
		}
		var s = String(val).replace(/^\s+|\s+$/g, '');
		if (!s.length)
		{
			return true;
		}
		var noNbspEntities = s
			.replace(/&nbsp;/gi, ' ')
			.replace(/&#160;/gi, ' ')
			.replace(/&#x0*A0;/gi, ' ');
		var plain = noNbspEntities
			.replace(/<[^>]*>/g, ' ')
			.replace(/\u00a0/g, ' ');
		plain = plain.replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
		return plain.length === 0;
	}

	function buildSpecificationsSections(item)
	{
		var sections = [];
		for (var g = 0; g < SPEC_FIELD_GROUPS.length; g++)
		{
			var grp = SPEC_FIELD_GROUPS[g];
			var rows = [];
			for (var r = 0; r < grp.rows.length; r++)
			{
				var row = grp.rows[r];
				var val = toDisplayString(getRawField(item, row.field));
				if (val && !isVacuousSpecificationValue(val))
				{
					rows.push({ label: row.label, value: val });
				}
			}
			if (rows.length)
			{
				sections.push({
					sectionTitle: grp.sectionTitle
				,	rows: rows
				});
			}
		}
		return sections;
	}

	function buildDocuments(item)
	{
		var out = [];
		for (var i = 1; i <= DOCUMENT_SLOT_COUNT; i++)
		{
			var title = toDisplayString(getRawField(item, 'custitem_tt_document_title_' + i));
			var src = toUrlString(getRawField(item, 'custitem_tt_document_file_src_' + i));
			var alt = toDisplayString(getRawField(item, 'custitem_tt_document_file_alt_' + i));
			if (title && src)
			{
				out.push({
					title: title
				,	src: src
				,	alt: alt || ''
				});
			}
		}
		return out;
	}

	function buildVideos(item)
	{
		var out = [];
		for (var i = 1; i <= VIDEO_SLOT_COUNT; i++)
		{
			var title = toDisplayString(getRawField(item, 'custitem_tt_video_title_' + i));
			var youtubeId = normalizeYoutubeVideoId(getRawField(item, 'custitem_tt_video_src_' + i));
			if (!title || !youtubeId)
			{
				continue;
			}
			var image = toUrlString(getRawField(item, 'custitem_tt_video_img_' + i));
			var alt = toDisplayString(getRawField(item, 'custitem_tt_video_alt_' + i));
			out.push({
				title: title
			,	youtubeId: youtubeId
			,	image: image || ''
			,	alt: alt || ''
			,	hasVideo: true
			});
		}
		return out;
	}

	function buildDescriptionHtml(item)
	{
		var primary = toHtmlString(getRawField(item, 'storedetaileddescription'));
		if (hasHtmlContent(primary))
		{
			return primary;
		}
		var fallback = toHtmlString(getRawField(item, 'custitem_packship_interdescription'));
		if (hasHtmlContent(fallback))
		{
			return fallback;
		}
		return null;
	}

	/**
	 * One label from a single class entry (string, number, or list/record–like object).
	 */
	function classEntryToLabel(entry)
	{
		if (entry === null || entry === undefined)
		{
			return null;
		}
		if (typeof entry === 'string' || typeof entry === 'number')
		{
			var s = String(entry).replace(/^\s+|\s+$/g, '');
			return s.length ? s : null;
		}
		if (typeof entry === 'object')
		{
			if (entry.text !== undefined && entry.text !== null && entry.text !== '')
			{
				return classEntryToLabel(entry.text);
			}
			if (entry.name !== undefined && entry.name !== null && entry.name !== '')
			{
				return classEntryToLabel(entry.name);
			}
			if (entry.value !== undefined && entry.value !== null && entry.value !== '' && typeof entry.value !== 'object')
			{
				return classEntryToLabel(entry.value);
			}
		}
		return null;
	}

	/**
	 * Each label becomes one or more pills: split on ":" (e.g. "Power Products : Solar Inverters").
	 */
	function expandCategoryLabelsSplitByColon(labels)
	{
		if (!labels || !labels.length)
		{
			return [];
		}
		var flat = [];
		for (var i = 0; i < labels.length; i++)
		{
			var s = labels[i];
			if (typeof s !== 'string')
			{
				continue;
			}
			var segs = s.split(':');
			for (var j = 0; j < segs.length; j++)
			{
				var p = segs[j].replace(/^\s+|\s+$/g, '');
				if (p.length)
				{
					flat.push(p);
				}
			}
		}
		return flat;
	}

	/**
	 * Item `class` → array of display strings for category pills (order preserved).
	 */
	function buildProductClassLabels(item)
	{
		var raw = getRawField(item, 'class');
		if (raw === null || raw === undefined || raw === '')
		{
			return [];
		}
		var base = [];
		if (typeof raw === 'string')
		{
			var trimmed = raw.replace(/^\s+|\s+$/g, '');
			if (!trimmed.length)
			{
				return [];
			}
			if (trimmed.indexOf(',') !== -1)
			{
				var parts = trimmed.split(',');
				for (var i = 0; i < parts.length; i++)
				{
					var p = parts[i].replace(/^\s+|\s+$/g, '');
					if (p.length)
					{
						base.push(p);
					}
				}
				return expandCategoryLabelsSplitByColon(base);
			}
			return expandCategoryLabelsSplitByColon([trimmed]);
		}
		if (Array.isArray(raw))
		{
			for (var j = 0; j < raw.length; j++)
			{
				var label = classEntryToLabel(raw[j]);
				if (label)
				{
					base.push(label);
				}
			}
			return expandCategoryLabelsSplitByColon(base);
		}
		if (typeof raw === 'object')
		{
			var single = classEntryToLabel(raw);
			return single ? expandCategoryLabelsSplitByColon([single]) : [];
		}
		return [];
	}

	function buildFeaturesHtml(item)
	{
		var primary = toHtmlString(getRawField(item, 'custitem_tt_features'));
		if (hasHtmlContent(primary))
		{
			return primary;
		}
		var fallback = toHtmlString(getRawField(item, 'custitem6'));
		if (hasHtmlContent(fallback))
		{
			return fallback;
		}
		return null;
	}

	/**
	 * Builds the normalized data object from the PDP item.
	 * @param {Object} item Item model (as returned by PDP.getItemInfo().item or equivalent).
	 * @return {Object}
	 */
	function normalizeProductTabsData(item)
	{
		var safeItem = item && typeof item === 'object' ? item : {};
		var plainItem = toPlainItemRecord(safeItem) || {};

		debugLog('normalizeProductTabsData: incoming item (type)', {
			isNull: item == null
		,	constructorName: item && item.constructor && item.constructor.name
		,	hasToJSON: item && typeof item.toJSON === 'function'
		,	hasGet: item && typeof item.get === 'function'
		,	hasAttributes: !!(item && item.attributes)
		});

		debugLog('Full item (raw, may be Model):', item);
		debugLog('Plain item (toPlainItemRecord / attributes):', plainItem);

		logFieldPresence(plainItem);

		var descriptionHtml = buildDescriptionHtml(plainItem);
		var productCategoryLabels = buildProductClassLabels(plainItem);
		var specificationsSections = buildSpecificationsSections(plainItem);
		var featuresHtml = buildFeaturesHtml(plainItem);
		var documents = buildDocuments(plainItem);
		var videos = buildVideos(plainItem);

		var result = {
			description: {
				html: descriptionHtml
			,	hasContent: hasHtmlContent(descriptionHtml)
			,	productCategoryLabels: productCategoryLabels
			,	hasProductCategory: productCategoryLabels.length > 0
			}
		,	specificationsSections: specificationsSections
		,	features: {
				html: featuresHtml
			,	hasContent: hasHtmlContent(featuresHtml)
			}
		,	documents: documents
		,	videos: videos
		};

		// Flat aliases useful for templates / debugging
		result.descriptionHtml = descriptionHtml;
		result.featuresHtml = featuresHtml;

		var visibleMeta = buildVisibleTabs(result);
		result.tabs = visibleMeta.tabs;
		result.defaultActiveId = visibleMeta.defaultActiveId;

		debugLog('normalizeProductTabsData result (summary):', {
			descriptionHasContent: result.description && result.description.hasContent
		,	productCategoryCount: productCategoryLabels.length
		,	specSectionCount: result.specificationsSections ? result.specificationsSections.length : 0
		,	featuresHasContent: result.features && result.features.hasContent
		,	documentsCount: result.documents ? result.documents.length : 0
		,	videosCount: result.videos ? result.videos.length : 0
		,	tabsCount: result.tabs ? result.tabs.length : 0
		,	tabIds: (result.tabs || []).map(function (t) { return t.id; })
		,	defaultActiveId: result.defaultActiveId
		});

		return result;
	}

	function tabHasContent(tabId, data)
	{
		switch (tabId)
		{
		case TAB_IDS.DESCRIPTION:
			return !!(
				data.description
			&&	(
					data.description.hasContent
				||	data.description.hasProductCategory
				)
			);
		case TAB_IDS.FEATURES_AND_SPECS:
			return !!(
				(data.specificationsSections && data.specificationsSections.length > 0)
			||	(data.features && data.features.hasContent)
			);
		case TAB_IDS.DOCUMENTS:
			return data.documents && data.documents.length > 0;
		case TAB_IDS.VIDEOS:
			return data.videos && data.videos.length > 0;
		default:
			return false;
		}
	}

	/**
	 * Defines which tabs are visible and which should be active by default.
	 * @param {Object} data Output of normalizeProductTabsData(item)
	 * @return {{ tabs: Array<{id: string}>, defaultActiveId: string|null }}
	 */
	function buildVisibleTabs(data)
	{
		if (!data)
		{
			return { tabs: [], defaultActiveId: null };
		}

		var tabs = [];
		for (var p = 0; p < TAB_PRIORITY.length; p++)
		{
			var id = TAB_PRIORITY[p];
			if (tabHasContent(id, data))
			{
				tabs.push({ id: id });
			}
		}

		var defaultActiveId = null;
		if (tabHasContent(TAB_IDS.DESCRIPTION, data))
		{
			defaultActiveId = TAB_IDS.DESCRIPTION;
		}
		else if (tabs.length)
		{
			defaultActiveId = tabs[0].id;
		}

		return {
			tabs: tabs
		,	defaultActiveId: defaultActiveId
		};
	}

	return {
		normalizeProductTabsData: normalizeProductTabsData
	,	buildVisibleTabs: buildVisibleTabs
	,	getRawField: getRawField
	,	toDisplayString: toDisplayString
	,	toUrlString: toUrlString
	,	toPlainItemRecord: toPlainItemRecord
	,	normalizeYoutubeVideoId: normalizeYoutubeVideoId
	,	buildYoutubeEmbedUrl: buildYoutubeEmbedUrl
	,	TAB_IDS: TAB_IDS
	};
});
